import { AiStars, FileArrowUp } from "@ignite-analytics/icons";
import { Button, Collapse, Stack, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { ACCEPTED_FILE_EXTENSIONS, MAX_FILE_SIZE } from "../components/Files/files";
import { useAlert } from "../lib/Alerts";

import type { Answer } from "./answers";
import { DocumentCard } from "./DocumentCard";
import { type UploadedFile, useFileActions } from "./fileActions";
import { type AnswerSuggestion, PREFILL_MAX_BYTES, PREFILL_MAX_FILES, prefillAccepts, useSuggestions } from "./prefill";
import { useReadOnly } from "./readOnly";

type Props = {
    questionKey: string;
    answer: Answer | undefined;
    multi?: boolean;
    files: string[];
    onChange: (update: (files: string[]) => string[]) => void;
    onUploaded?: (uploaded: UploadedFile[]) => void;
    error?: boolean;
    children?: React.ReactNode;
};

const accepted = (file: File) =>
    ACCEPTED_FILE_EXTENSIONS.split(",").includes(file.name.substring(file.name.lastIndexOf(".")).toLowerCase());

export const FilePicker: React.FC<Props> = ({
    questionKey,
    answer,
    multi,
    files,
    onChange,
    onUploaded,
    error,
    children,
}) => {
    const { formatMessage } = useIntl();
    const { alertUser } = useAlert();
    const actions = useFileActions();
    const suggestions = useSuggestions();
    const input = useRef<HTMLInputElement>(null);
    const readOnly = useReadOnly();
    const [busy, setBusy] = useState(false);
    const [reading, setReading] = useState<string[]>([]);
    const [found, setFound] = useState<AnswerSuggestion[]>([]);
    const [prompt, setPrompt] = useState(false);
    const prompting = suggestions?.prompting;

    useEffect(() => {
        prompting?.(questionKey, prompt);
        return () => prompting?.(questionKey, false);
    }, [prompting, questionKey, prompt]);
    const [sizes, setSizes] = useState<{ name: string; size: number }[]>([]);
    const canAdd = (multi || files.length === 0) && !busy;

    const fail = (value: string) => alertUser({ value, severity: "error" });

    const add = async (picked: File[]) => {
        const rejected = picked.filter((f) => !accepted(f) || f.size > MAX_FILE_SIZE);
        if (rejected.length > 0) {
            fail(
                formatMessage(
                    {
                        defaultMessage:
                            "{files} cannot be uploaded. Files must be under 30 MB and of a supported type.",
                    },
                    { files: rejected.map((f) => f.name).join(", ") }
                )
            );
        }
        const valid = picked.filter((f) => !rejected.includes(f)).slice(0, multi ? undefined : 1);
        if (valid.length === 0) return;
        if (!actions) {
            onChange((current) => [...current, ...valid.map((f) => f.name).filter((name) => !current.includes(name))]);
            return;
        }
        setBusy(true);
        try {
            const results = await Promise.allSettled(valid.map((f) => actions.upload(questionKey, answer, f)));
            const uploaded = results.flatMap((r) => (r.status === "fulfilled" ? [r.value] : []));
            setSizes((current) => [
                ...current,
                ...results.flatMap((r, i) =>
                    r.status === "fulfilled" ? [{ name: r.value.fileName, size: valid[i].size }] : []
                ),
            ]);
            const readable = results
                .flatMap((r, i) =>
                    r.status === "fulfilled" && prefillAccepts(r.value.fileName) && valid[i].size <= PREFILL_MAX_BYTES
                        ? [r.value]
                        : []
                )
                .slice(0, PREFILL_MAX_FILES);
            if (suggestions && readable.length > 0) {
                setReading((current) => [...current, ...readable.map((r) => r.fileName)]);
                actions
                    .suggestAnswers(questionKey, readable)
                    .then((list) => {
                        const usable = suggestions.applicable(list);
                        setFound(usable);
                        setPrompt(usable.length > 0);
                    })
                    .catch(() => undefined)
                    .finally(() =>
                        setReading((current) => current.filter((name) => !readable.some((r) => r.fileName === name)))
                    );
            }
            if (uploaded.length < valid.length) {
                fail(formatMessage({ defaultMessage: "File upload failed. Please try again." }));
            }
            if (uploaded.length > 0) {
                onChange((current) => [...current, ...uploaded.map((u) => u.fileName)]);
                onUploaded?.(uploaded);
            }
        } finally {
            setBusy(false);
        }
    };

    const remove = async (name: string) => {
        if (actions) {
            setBusy(true);
            try {
                await actions.remove(questionKey, name);
            } catch {
                fail(formatMessage({ defaultMessage: "Could not remove the file. Please try again." }));
                return;
            } finally {
                setBusy(false);
            }
        }
        onChange((current) => current.filter((f) => f !== name));
        const left = found.filter((s) => s.fileName !== name);
        setFound(left);
        setPrompt((open) => open && left.length > 0);
    };

    const download = (name: string) =>
        actions
            ?.download(questionKey, name)
            .catch(() => fail(formatMessage({ defaultMessage: "Could not download the file. Please try again." })));

    return (
        <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
            {files.length > 0 && (
                <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1.5 }}>
                    {files.map((name) => (
                        <DocumentCard
                            key={name}
                            name={name}
                            sizeBytes={sizes.find((s) => s.name === name)?.size}
                            analyzing={reading.includes(name)}
                            onOpen={() => download(name)}
                            onRemove={readOnly ? undefined : () => remove(name)}
                            removing={busy}
                        />
                    ))}
                </Stack>
            )}
            {!readOnly && (canAdd || busy) && (
                <Button
                    size="small"
                    color={error ? "error" : "secondary"}
                    variant="outlined"
                    startIcon={<FileArrowUp />}
                    loading={busy}
                    disabled={!canAdd}
                    onClick={() => input.current?.click()}
                >
                    {multi ? (
                        <FormattedMessage defaultMessage="Upload files" />
                    ) : (
                        <FormattedMessage defaultMessage="Upload file" />
                    )}
                </Button>
            )}
            {children && (
                <Stack spacing={2} sx={{ alignSelf: "stretch", pt: 1 }}>
                    {children}
                </Stack>
            )}
            {suggestions && (
                <Collapse
                    in={prompt}
                    unmountOnExit
                    sx={{
                        alignSelf: "stretch",
                        position: { lg: "absolute" },
                        top: { lg: 0 },
                        left: { lg: "calc(100% + 49px)" },
                        width: { lg: 260 },
                    }}
                >
                    <Stack
                        direction={{ xs: "row", lg: "column" }}
                        spacing={1.5}
                        sx={{
                            alignItems: { xs: "center", lg: "stretch" },
                            px: 1.5,
                            py: 1.25,
                            borderRadius: 1.5,
                            border: "1px solid",
                            borderColor: "divider",
                            bgcolor: "background.paper",
                            boxShadow: "0 1px 2px rgba(13, 16, 23, 0.04)",
                        }}
                    >
                        <Stack direction="row" spacing={1} sx={{ flex: 1, alignItems: "flex-start" }}>
                            <AiStars
                                fontSize="small"
                                sx={{ color: (theme) => theme.palette.tokens?.icon.iconPurple }}
                            />
                            <Typography variant="textSm">
                                <FormattedMessage
                                    defaultMessage="Found answers to {count, plural, one {# other question} other {# other questions}} in this document."
                                    values={{ count: found.length }}
                                />
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
                            <Button size="small" color="secondary" onClick={() => setPrompt(false)}>
                                <FormattedMessage defaultMessage="Dismiss" />
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={() => {
                                    suggestions.apply(found);
                                    setPrompt(false);
                                }}
                            >
                                <FormattedMessage defaultMessage="Apply" />
                            </Button>
                        </Stack>
                    </Stack>
                </Collapse>
            )}
            <input
                ref={input}
                type="file"
                hidden
                multiple={multi}
                accept={ACCEPTED_FILE_EXTENSIONS}
                onChange={(event) => {
                    const picked = Array.from(event.target.files ?? []);
                    event.target.value = "";
                    add(picked);
                }}
            />
        </Stack>
    );
};
